import { Fragment, useState, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { FieldArray } from 'react-final-form-arrays'
import PricingRow from './PricingRow'
import { FormattedMessage } from 'react-intl'
import TravelDatepicker from '../../helpers/TravelDatepicker'
import moment from 'moment'

import './pricing-form.scss'
import messages from '../../../messages'
import DateFormat from '../../helpers/DateFormat'
import PriceConverter, { convertCurrency } from '../../helpers/PriceConverter'
import get from 'lodash/get'
import { APP_LANGUAGE, createUrl } from '../../../localized-urls'
import store from 'store2'
import { generateQueries, parseQuery } from '../../../utils'
import useAvailableDates from '../hooks/useAvailableDates'

import PropTypes from 'prop-types'
import { setAdventureDate } from '../../../actions/adventure-actions'

const chineeseLangs = ['zh-hans', 'zh-hant']

function PricingForm({
  handleSubmit,
  values,
  change,
  activityId,
  selectedCurrency,
}) {
  const dispatch = useDispatch()
  const { dates, availabilities } = useAvailableDates(
    activityId,
    (availableDates) => {
      if(!availableDates.includes(values.startDate)) {
        change('startDate', null)
      }
    },
  )
  const [showTooltip, setTooltipVisibility] = useState(false)
  const { inclusions, participants, startDate } = values
  const openCalendar = () => {
    document.querySelector('.travel-datepicker').click()
  }
  const query = parseQuery()
  const linkedDiveSite = query.dive_site_id
  const linkedSiteGuaranteed = linkedDiveSite && window.diveSiteGuaranteed

  const onDateSelect = ({ dateFrom }) => {
    dispatch(setAdventureDate(dateFrom))
    change('startDate', dateFrom)
    if(dateFrom) {
      store('date', [
        dateFrom,
        moment(dateFrom).add(7, 'days').format('YYYY-MM-DD'),
      ])
      store('departure_date_custom', dateFrom)
    } else {
      store.remove('date')
      store.remove('departure_date_custom')
    }
  }
  const participantsQty = get(values, 'participants.quantity')
  const handleBookNow = () => {
    if(!values.startDate) {
      return openCalendar()
    }

    if(!participantsQty) {
      return setTooltipVisibility(true)
    }

    const originValue = get(participants, 'price.origin_value', 0)
    const originCurrency = get(participants, 'price.origin_currency', '')

    const ecomerceProducts = [
      {
        name: window.activityTitleEn,
        id: +window.activityId,
        price: convertCurrency(
          originValue,
          originCurrency,
          selectedCurrency,
          false,
          true,
        ).toFixed(2),
        brand: `${window.diveCenterTitle}|${window.affiliateId}`,
        category: `${window.activityType} (Adventures)`,
        quantity: participantsQty,
      },
    ]

    const inclusions = values.inclusions.reduce((acc, inclusion) => {
      if(inclusion.quantity) {
        ecomerceProducts.push({
          name: inclusion.title_en,
          id: inclusion.extra_option_id,
          price: convertCurrency(
            inclusion.price.origin_value,
            inclusion.price.origin_currency,
            selectedCurrency,
            false,
            true,
          ).toFixed(2),
          brand: `${window.diveCenterTitle}|${window.affiliateId}`,
          category: `${window.activityType} (Adventures)`,
          quantity: inclusion.quantity,
        })

        return [...acc, [`${inclusion.extra_option_id},${inclusion.quantity}`]]
      }
      return acc
    }, '')

    const newQuery = generateQueries({
      participants: participantsQty,
      widget: window.isWidget,
      utm_medium: window.isWidget && 'widget',
      single_product: window.isSingleProduct,
      inclusions,
    })

    chineeseLangs.includes(APP_LANGUAGE)
      ? (window.location.href = `${window.checkoutUrl}${values.startDate}/${newQuery}&source_cn=CHINA_CLUB`)
      : (window.location.href = createUrl(
        'checkout_adventures',
        { date: values.startDate, activityId },
        newQuery,
      ))

    return true
  }

  const total = useMemo(() => {
    const { quantity, price } = values.participants
    const participantsPrice = quantity * price.origin_value
    const inclusionsPrice = values.inclusions.reduce((acc, item) => {
      if(item.quantity) {
        return acc + item.quantity * parseFloat(item.price.origin_value)
      }
      return acc
    }, 0)

    if(quantity) {
      setTooltipVisibility(false)
    }
    return {
      totalAmount: participantsPrice + inclusionsPrice,
      currency: price.origin_currency,
    }
  }, [values.participants, values.inclusions])

  const maximumAvailability = useMemo(() => {
    if(availabilities) {
      const selectedDate =
        availabilities.find((item) => item.date === startDate) || {}
      return selectedDate.availableSeats
    }
  }, [startDate, availabilities])

  return (
    <form onSubmit={handleSubmit}>
      <div className='activity-date-field'>
        <FormattedMessage
          tagName='h4'
          id='activity_date'
          defaultMessage='Activity date'
        />
        <TravelDatepicker
          labelFrom={messages.selectDate}
          className='travel-datepicker'
          availableDatesAdventures={dates}
          disableInput
          closeOnSelect
          dateFrom={startDate}
          callback={onDateSelect}
          isDiveCenterCalendar
          showLegend
        />
        <div
          className={`activity-date-field__input ${!startDate && 'has-error'}`}
          onClick={openCalendar}
        >
          <i className='font-icons calendar-icon' />
          {startDate ? (
            <DateFormat date={startDate} />
          ) : (
            <FormattedMessage
              id='view_available_date'
              defaultMessage={'View available dates'}
            />
          )}
        </div>
      </div>
      <h4 className='activity-pricing__section-title'>
        <FormattedMessage id='quantity' defaultMessage='Quantity' />
      </h4>
      <PricingRow
        name='participants.quantity'
        item={participants}
        availabilities={availabilities}
        maximumAvailability={maximumAvailability}
        showTooltip={showTooltip}
      />
      {participants.quantity >= maximumAvailability && (
        <div className='warning-message mts mbn'>
          <FormattedMessage
            id='maximum_limit'
            defaultMessage='There are only {qty, plural, one {# spot} other {# spots}} left on that date'
            values={{ qty: maximumAvailability }}
          />
        </div>
      )}
      {!!(inclusions && inclusions.length) && (
        <Fragment>
          <h4 className='activity-pricing__section-title'>
            <FormattedMessage id='extras' defaultMessage='Extras' />
          </h4>
          <FieldArray name='inclusions'>
            {({ fields }) => (
              <div>
                {fields.map((name, index) => (
                  <PricingRow
                    key={name}
                    name={`${name}.quantity`}
                    item={inclusions[index]}
                  />
                ))}
              </div>
            )}
          </FieldArray>
        </Fragment>
      )}
      <div className='activity-form__footer'>
        <p className='activity-form__total-price'>
          <PriceConverter
            decimals
            amount={total.totalAmount}
            currency={total.currency}
            conversionPercent
          />
        </p>
        <button
          className='btn-red'
          id='book-now-button'
          onClick={handleBookNow}
        >
          <FormattedMessage id='book_now' defaultMessage='Book now' />
        </button>
        {linkedDiveSite && linkedSiteGuaranteed && (
          <p className='activity-form__warning'>
            <FormattedMessage
              id='adventure.pricing_form.operator_ds_guaranteed'
              defaultMessage='We will request that the operator runs this activity at the dive site you have selected, but this is not guaranteed.'
            />
          </p>
        )}
      </div>
    </form>
  )
}

export default connect(({ selectedCurrency }) => ({
  selectedCurrency,
}))(PricingForm)

PricingForm.propTypes = {
  handleSubmit: PropTypes.func,
  values: PropTypes.object,
  change: PropTypes.func,
  activityId: PropTypes.string,
  selectedCurrency: PropTypes.string,
}
